'use client';

import { useEffect } from 'react';
import { useAnimate, useReducedMotion } from 'motion/react';
import { cn } from 'utils/styles';

interface ExplainerProgressProps {
  total: number;
  current: number;
  duration: number;
  isPlaying: boolean;
  cardIsDark: boolean;
  onSelect: (index: number) => void;
}

const ActiveFill = ({ duration }: { duration: number }) => {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const animation = animate(scope.current, { width: ['0%', '100%'] }, { duration, ease: 'linear' });
    return () => animation.cancel();
  }, [animate, scope, duration]);

  return <div ref={scope} className="h-full rounded-full bg-verde" />;
};

const ExplainerProgress = ({ total, current, duration, isPlaying, cardIsDark, onSelect }: ExplainerProgressProps) => {
  const prefersReducedMotion = useReducedMotion();
  const trackColor = cardIsDark ? 'bg-[#ffffff]/40' : 'bg-scrim/20';

  return (
    <div className="carousel-progress flex items-center gap-2">
      {Array.from({ length: total }, (_, index) => {
        const isComplete = index < current;
        const isActive = index === current;
        const showStaticFill = isComplete || (isActive && (!isPlaying || prefersReducedMotion));

        return (
          <button
            key={index}
            type="button"
            onClick={() => onSelect(index)}
            aria-label={`Go to slide ${index + 1}`}
            className="flex-1 cursor-pointer py-2">
            <span className={cn('block h-1 w-full overflow-hidden rounded-full', trackColor)}>
              {showStaticFill ? (
                <span className="block h-full w-full rounded-full bg-verde" />
              ) : isActive ? (
                <ActiveFill key={current} duration={duration} />
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default ExplainerProgress;
